/**
 * Admin Watermarks Management
 * Upload and manage 4 corner watermarks for video clips
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Upload, Image, Trash2, Save, Eye, 
  EyeOff, RefreshCw, CheckCircle, AlertCircle,
  Settings, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// Position config
const POSITIONS = [
  { id: 'top-left', label: 'Superior Izquierda', corner: 'tl' },
  { id: 'top-right', label: 'Superior Derecha', corner: 'tr' },
  { id: 'bottom-left', label: 'Inferior Izquierda', corner: 'bl' },
  { id: 'bottom-right', label: 'Inferior Derecha', corner: 'br' }
];

// Watermark card component
const WatermarkCard = ({ watermark, position, onUpload, onUpdate, onDelete, onPreview, loading }) => {
  const fileInputRef = useRef(null);
  const [localEnabled, setLocalEnabled] = useState(watermark?.enabled || false);
  const [localOpacity, setLocalOpacity] = useState(watermark?.opacity || 0.5);
  const [localScale, setLocalScale] = useState(watermark?.scale || 0.1);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Create a stable identifier for watermark changes
  const watermarkKey = useMemo(() => 
    watermark ? `${watermark.enabled}-${watermark.opacity}-${watermark.scale}` : 'none',
    [watermark]
  );
  
  // Use effect with the stable key to sync state
  useEffect(() => {
    if (watermark) {
      setLocalEnabled(watermark.enabled || false);
      setLocalOpacity(watermark.opacity || 0.5);
      setLocalScale(watermark.scale || 0.1);
      setHasChanges(false);
    }
  }, [watermarkKey]);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5MB');
      return;
    }
    
    try {
      setUploading(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        await onUpload(position.id, base64, localEnabled, localOpacity, localScale);
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error('Error al leer el archivo');
        setUploading(false);
      };
      reader.readAsDataURL(file);
      
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Error al subir imagen');
      setUploading(false);
    }
    
    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleToggleEnabled = (enabled) => {
    setLocalEnabled(enabled);
    setHasChanges(true);
  };
  
  const handleOpacityChange = (value) => {
    setLocalOpacity(value[0]);
    setHasChanges(true);
  };
  
  const handleScaleChange = (value) => {
    setLocalScale(value[0]);
    setHasChanges(true);
  };
  
  const handleSaveChanges = async () => {
    await onUpdate(position.id, {
      enabled: localEnabled,
      opacity: localOpacity,
      scale: localScale
    });
    setHasChanges(false);
  };
  
  const positionStyles = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl p-4 space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">{position.label}</h3>
          <p className="text-xs text-muted-foreground">
            {watermark?.has_image ? 'Imagen configurada' : 'Sin imagen'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={localEnabled}
            onCheckedChange={handleToggleEnabled}
            disabled={!watermark?.has_image}
          />
          <span className="text-xs text-muted-foreground">
            {localEnabled ? 'Activo' : 'Inactivo'}
          </span>
        </div>
      </div>
      
      {/* Preview area */}
      <div className="relative aspect-video bg-secondary/50 rounded-lg overflow-hidden border border-border">
        {/* Video placeholder */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-muted-foreground">Vista previa del video</span>
        </div>
        
        {/* Watermark preview position with actual image */}
        {watermark?.has_image && watermark?.image_preview && (
          <div 
            className={`absolute ${positionStyles[position.id]} p-1`}
            style={{ opacity: localOpacity }}
          >
            <img 
              src={watermark.image_preview} 
              alt={`Watermark ${position.label}`}
              className="max-w-16 max-h-12 object-contain rounded"
              style={{ transform: `scale(${localScale * 5})` }}
            />
          </div>
        )}
        
        {/* Placeholder if no preview */}
        {watermark?.has_image && !watermark?.image_preview && (
          <div 
            className={`absolute ${positionStyles[position.id]} w-12 h-12 bg-white/20 rounded flex items-center justify-center border border-white/30`}
            style={{ opacity: localOpacity }}
          >
            <Image className="w-6 h-6 text-white/70" />
          </div>
        )}
      </div>
      
      {/* Upload/Preview buttons */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <Button
          variant="outline"
          size="sm"
          className="flex-1 gap-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || loading}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {watermark?.has_image ? 'Cambiar' : 'Subir'}
        </Button>
        
        {watermark?.has_image && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreview(position.id)}
              disabled={loading}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(position.id)}
              disabled={loading}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
      
      {/* Settings sliders */}
      {watermark?.has_image && (
        <div className="space-y-4 pt-2 border-t border-border">
          {/* Opacity */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Opacidad</span>
              <span>{Math.round(localOpacity * 100)}%</span>
            </div>
            <Slider
              value={[localOpacity]}
              onValueChange={handleOpacityChange}
              min={0.1}
              max={1}
              step={0.05}
              className="w-full"
            />
          </div>
          
          {/* Scale */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tamaño</span>
              <span>{Math.round(localScale * 100)}%</span>
            </div>
            <Slider
              value={[localScale]}
              onValueChange={handleScaleChange}
              min={0.05}
              max={0.3}
              step={0.01}
              className="w-full"
            />
          </div>
          
          {/* Save button */}
          <AnimatePresence>
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Button
                  size="sm"
                  className="w-full gap-2"
                  onClick={handleSaveChanges}
                  disabled={loading}
                >
                  <Save className="w-4 h-4" />
                  Guardar cambios
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default function AdminWatermarks() {
  const navigate = useNavigate();
  const [watermarks, setWatermarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  useEffect(() => {
    fetchWatermarks();
  }, []);
  
  const fetchWatermarks = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getWatermarks();
      setWatermarks(res.data.watermarks || []);
      setLastUpdated(res.data.updated_at);
    } catch (err) {
      console.error('Error fetching watermarks:', err);
      toast.error('Error al cargar watermarks');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpload = async (position, imageData, enabled, opacity, scale) => {
    try {
      await adminAPI.uploadWatermark({
        position,
        image_data: imageData,
        enabled,
        opacity,
        scale
      });
      toast.success('Watermark subido exitosamente');
      await fetchWatermarks();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Error al subir watermark');
      throw err;
    }
  };
  
  const handleUpdate = async (position, settings) => {
    try {
      await adminAPI.updateWatermark(position, {
        position,
        ...settings,
        type: 'image'
      });
      toast.success('Configuración actualizada');
      await fetchWatermarks();
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Error al actualizar configuración');
    }
  };
  
  const handleDelete = async (position) => {
    if (!window.confirm('¿Eliminar este watermark?')) return;
    
    try {
      await adminAPI.deleteWatermark(position);
      toast.success('Watermark eliminado');
      await fetchWatermarks();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error al eliminar watermark');
    }
  };
  
  const handlePreview = async (position) => {
    try {
      // Get the image using the API with authentication
      const token = localStorage.getItem('clipper_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/admin/watermarks/${position}/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('No se pudo cargar la imagen');
      }
      
      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      // Open in a new window or show in modal
      const previewWindow = window.open('', '_blank', 'width=400,height=400');
      if (previewWindow) {
        previewWindow.document.write(`
          <html>
            <head><title>Watermark Preview</title></head>
            <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#18181B;">
              <img src="${imageUrl}" style="max-width:100%;max-height:100%;object-fit:contain;" />
            </body>
          </html>
        `);
      }
    } catch (err) {
      toast.error('Error al cargar vista previa');
    }
  };
  
  const getWatermarkForPosition = (positionId) => {
    return watermarks.find(w => w.position === positionId) || null;
  };
  
  const activeCount = watermarks.filter(w => w.enabled && w.has_image).length;
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Watermarks</h1>
              <p className="text-xs text-muted-foreground">Logos en videos</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchWatermarks}
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>
      
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Info banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
              <Image className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-medium">Marcas de agua para videos</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Sube logos PNG/JPG para cada esquina del video. Se aplicarán automáticamente 
                a todos los clips generados. Recomendamos imágenes con fondo transparente (PNG).
              </p>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{activeCount} activos</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Settings className="w-4 h-4 text-muted-foreground" />
                  <span>4 posiciones disponibles</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Last updated */}
        {lastUpdated && (
          <p className="text-xs text-muted-foreground text-center">
            Última actualización: {new Date(lastUpdated).toLocaleString('es-MX')}
          </p>
        )}
        
        {/* Watermark grid */}
        {loading && watermarks.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {POSITIONS.map((position) => (
              <WatermarkCard
                key={position.id}
                position={position}
                watermark={getWatermarkForPosition(position.id)}
                onUpload={handleUpload}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onPreview={handlePreview}
                loading={loading}
              />
            ))}
          </div>
        )}
        
        {/* Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-secondary/30 rounded-xl p-4 space-y-2"
        >
          <h3 className="font-medium text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            Recomendaciones
          </h3>
          <ul className="text-xs text-muted-foreground space-y-1 ml-6">
            <li>• Usa imágenes PNG con fondo transparente para mejores resultados</li>
            <li>• Tamaño recomendado: 200x200 px o superior</li>
            <li>• Los watermarks se escalan automáticamente según el tamaño del video</li>
            <li>• Ajusta la opacidad para que no obstruya la visión del juego</li>
            <li>• Los cambios se aplican a los nuevos clips, no a los existentes</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
